import { useState } from 'react';
import { Dialog } from '@headlessui/react';
import { FeatureFlag, DEFAULT_FEATURE_FLAGS } from '../../utils/featureFlags';
import { useTranslation } from 'react-i18next';
import { Company } from '../../types/company.types';

interface CompanyModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (company: Partial<Company>) => void;
  company?: Company;
}

const CompanyModal = ({ isOpen, onClose, onSubmit, company }: CompanyModalProps) => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState<Partial<Company>>({
    name: company?.name || '',
    openrouter_api_key: company?.openrouter_api_key || '',
    feature_flags: company?.feature_flags || { ...DEFAULT_FEATURE_FLAGS },
    custobar_company_name: company?.custobar_company_name || '',
    custobar_api_key: company?.custobar_api_key || ''
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit(formData);
    onClose();
    resetFormData();
  };

  const resetFormData = () => {
    setFormData({
      name: '',
      openrouter_api_key: '',
      feature_flags: { ...DEFAULT_FEATURE_FLAGS },
      custobar_company_name: '',
      custobar_api_key: '',
    });
  };

  const toggleFeatureFlag = (flag: FeatureFlag) => {
    setFormData({
      ...formData,
      feature_flags: {
        ...formData.feature_flags,
        [flag]: !(formData.feature_flags?.[flag] ?? DEFAULT_FEATURE_FLAGS[flag]),
      },
    });
  };

  return (
    <Dialog open={isOpen} onClose={onClose} className="relative z-50">
      <div className="fixed inset-0 bg-black/30" aria-hidden="true" />

      <div className="fixed inset-0 flex items-center justify-center p-4">
        <Dialog.Panel className="bg-white rounded-lg p-6 w-full max-w-md max-h-[90vh] overflow-y-auto">
          <Dialog.Title className="text-xl font-semibold mb-4">
            {company ? t('company.edit_company') : t('company.create_new_company')}
          </Dialog.Title>

          <form onSubmit={handleSubmit}>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700">
                  {t('company.company_name')}
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">
                  {t('company.openrouter_api_key')}
                </label>
                <input
                  type="text"
                  value={formData.openrouter_api_key ?? ''}
                  onChange={(e) => setFormData({ ...formData, openrouter_api_key: e.target.value })}
                  className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2"
                  required
                />
              </div>

              {/* Conditional Custobar fields */}
              {formData.feature_flags?.[FeatureFlag.CUSTOBAR_DATA_INGESTION] && (
                <div className="border rounded-md p-4 bg-gray-50">
                  <h3 className="text-md font-medium mb-2">{t('company.custobar_fields_info')}</h3>
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700">
                        {t('company.custobar_company_name')}
                      </label>
                      <input
                        type="text"
                        value={formData.custobar_company_name || ''}
                        onChange={(e) =>
                          setFormData({ ...formData, custobar_company_name: e.target.value })
                        }
                        className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">
                        {t('company.custobar_api_key')}
                      </label>
                      <input
                        type="password"
                        value={formData.custobar_api_key || ''}
                        onChange={(e) =>
                          setFormData({ ...formData, custobar_api_key: e.target.value })
                        }
                        className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2"
                        required
                      />
                    </div>
                  </div>
                </div>
              )}

              <div className="mt-6">
                <h3 className="text-md font-medium">{t('company.feature_flags')}</h3>
                <p className="text-xs text-gray-500 mb-2">{t('company.feature_flags_details')}</p>
                <div className="space-y-2 border rounded-md p-3">
                  {Object.values(FeatureFlag).map((flag) => (
                    <label key={flag} className="flex items-center">
                      <input
                        type="checkbox"
                        checked={formData.feature_flags?.[flag] ?? DEFAULT_FEATURE_FLAGS[flag]}
                        onChange={() => toggleFeatureFlag(flag)}
                        className="rounded border-gray-300 text-blue-600 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                      />
                      <span className="ml-2 text-sm text-gray-700">{t(`features.${flag}`)}</span>
                    </label>
                  ))}
                </div>
              </div>

              <div className="flex justify-end space-x-3 mt-6">
                <button
                  type="button"
                  onClick={() => {
                    onClose();
                    resetFormData();
                  }}
                  className="px-2 py-1 text-sm text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
                >
                  {t('common.cancel')}
                </button>
                <button
                  type="submit"
                  className="px-2 py-1 text-sm text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700"
                >
                  {company ? t('common.save_changes') : t('company.create_company')}
                </button>
              </div>
            </div>
          </form>
        </Dialog.Panel>
      </div>
    </Dialog>
  );
};

export default CompanyModal;
